// Which support actions apply to a runner, and the words the console uses
// for each (handoff "Action copy", adjusted where Paceroo behaves differently).

import { DESTRUCTIVE_ACTIONS, type AdminRunnerDetail, type SupportAction } from "@paceroo/domain/views/admin";

export interface ActionSpec {
  action: SupportAction;
  label: string;
  /** Button text. */
  btn: string;
  /** What it does, for the row and the dialog. */
  what: string;
  destructive: boolean;
  /** Needs a choice before it can run. */
  pick?: { kind: "assistant" | "plan"; options: Array<{ id: string; label: string }> };
}

export function applicableActions(d: AdminRunnerDetail): ActionSpec[] {
  const r = d.runner;
  const archived = d.plans.filter((p) => p.archivedAt);
  const activatable = d.plans.filter((p) => !p.archivedAt && !p.current);
  const active = d.plans.find((p) => p.current);
  const assistantLabel = (a: AdminRunnerDetail["assistants"][number]) => `${a.name ?? "Unnamed app"}${a.host ? ` (${a.host})` : ""}`;
  const out: ActionSpec[] = [];
  const add = (spec: Omit<ActionSpec, "destructive">) => out.push({ ...spec, destructive: DESTRUCTIVE_ACTIONS.includes(spec.action) });

  if (!r.emailVerified) {
    add({ action: "verify-email", label: "Verify email", btn: "Verify email", what: `Marks ${r.email} as verified without a link. Use only when you have confirmed the address another way.` });
    add({ action: "resend-verification", label: "Resend verification", btn: "Resend", what: `Sends a new verification link to ${r.email}.` });
  }
  if (r.methods.includes("password")) {
    add({ action: "send-password-reset", label: "Send password reset", btn: "Send reset", what: "Emails a reset link valid for 1 hour. Their current password keeps working until they use it." });
  }
  if (r.signInLocked) {
    add({ action: "clear-rate-limit", label: "Clear rate-limit lock", btn: "Clear lock", what: "Removes the sign-in lock after repeated failed attempts. They can sign in straight away." });
  }
  if (d.sessions.length > 0) {
    add({
      action: "revoke-sessions",
      label: "Revoke all sessions",
      btn: "Revoke sessions",
      what: `Signs them out on every device (${d.sessions.length} active). Connected assistants stay connected.`,
    });
  }
  if (d.assistants.length > 0) {
    const only = d.assistants.length === 1 ? d.assistants[0]! : null;
    add({
      action: "disconnect-app",
      label: "Disconnect an assistant",
      btn: "Disconnect",
      what: `Removes ${only ? `${assistantLabel(only)}’s` : "the chosen assistant’s"} access. The assistant has to ask for consent again to reconnect.`,
      pick: { kind: "assistant", options: d.assistants.map((a) => ({ id: a.clientId, label: assistantLabel(a) })) },
    });
  }
  if (archived.length > 0) {
    add({
      action: "restore-plan",
      label: "Restore archived plan",
      btn: "Restore",
      what: "Moves the plan back to their plans list. It does not become the active plan.",
      pick: { kind: "plan", options: archived.map((p) => ({ id: p.id, label: p.name })) },
    });
  }
  if (activatable.length > 0) {
    add({
      action: "set-active-plan",
      label: "Set active plan",
      btn: "Set active",
      what: `Makes the chosen plan the active one.${active ? ` “${active.name}” stays in their plans list.` : ""}`,
      pick: { kind: "plan", options: activatable.map((p) => ({ id: p.id, label: p.name })) },
    });
  }
  if (r.role !== "admin") {
    if (r.banned) add({ action: "unban", label: "Unban runner", btn: "Unban", what: "Lets them sign in again. Sessions and assistants removed by the ban stay removed." });
    else add({ action: "ban", label: "Ban runner", btn: "Ban runner", what: "Signs them out everywhere, blocks sign-in and disconnects assistants. Their data is kept. You can unban later." });
    add({
      action: "delete-account",
      label: "Delete account",
      btn: "Delete account",
      what: `Permanently deletes the account, ${d.plans.length} plan${d.plans.length === 1 ? "" : "s"} and assistant connections. This cannot be undone.`,
    });
  }
  return out;
}

/** The danger zone: ban / unban / delete. */
export const isDanger = (a: SupportAction) => a === "ban" || a === "unban" || a === "delete-account";
